import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const usePerformanceMonitoring = () => {
    const location = useLocation();

    useEffect(() => {
        // Monitor navigation timing
        const navigationTiming = () => {
            const timing = window.performance.timing;
            const navigationStart = timing.navigationStart;

            const metrics = {
                dnsLookup: timing.domainLookupEnd - timing.domainLookupStart,
                tcpConnection: timing.connectEnd - timing.connectStart,
                serverResponse: timing.responseEnd - timing.requestStart,
                domLoading: timing.domLoading - navigationStart,
                domInteractive: timing.domInteractive - navigationStart,
                domComplete: timing.domComplete - navigationStart,
                loadComplete: timing.loadEventEnd - navigationStart,
                firstPaint: performance.getEntriesByType('paint')[0]?.startTime,
                firstContentfulPaint: performance.getEntriesByType('paint')[1]?.startTime
            };

            // Log metrics (replace with your analytics service)
            console.info('Performance Metrics:', {
                path: location.pathname,
                ...metrics
            });
        };

        // Monitor resource loading
        const resourceTiming = () => {
            const resources = performance.getEntriesByType('resource');
            const slowResources = resources.filter(resource => resource.duration > 1000);

            if (slowResources.length > 0) {
                console.warn('Slow Resources:', slowResources.map(resource => ({
                    name: resource.name,
                    duration: resource.duration,
                    size: resource.transferSize
                })));
            }
        };

        // Monitor Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((entryList) => {
            const entries = entryList.getEntries();
            const lastEntry = entries[entries.length - 1];
            console.info('Largest Contentful Paint:', {
                path: location.pathname,
                value: lastEntry.startTime,
                element: lastEntry.element
            });
        });

        // Monitor First Input Delay
        const fidObserver = new PerformanceObserver((entryList) => {
            const entries = entryList.getEntries();
            entries.forEach(entry => {
                console.info('First Input Delay:', {
                    path: location.pathname,
                    value: entry.processingStart - entry.startTime
                });
            });
        });

        // Monitor Cumulative Layout Shift
        const clsObserver = new PerformanceObserver((entryList) => {
            let clsValue = 0;
            const entries = entryList.getEntries();
            entries.forEach(entry => {
                if (!entry.hadRecentInput) {
                    clsValue += entry.value;
                }
            });
            console.info('Cumulative Layout Shift:', {
                path: location.pathname,
                value: clsValue
            });
        });

        try {
            // Check for supported entry types before observing
            if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
                lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
            }
            if (PerformanceObserver.supportedEntryTypes.includes('first-input')) {
                fidObserver.observe({ entryTypes: ['first-input'] });
            }
            if (PerformanceObserver.supportedEntryTypes.includes('layout-shift')) {
                clsObserver.observe({ entryTypes: ['layout-shift'] });
            }

            // Monitor initial load
            window.addEventListener('load', () => {
                navigationTiming();
                resourceTiming();
            });
        } catch (error) {
            console.warn('Performance monitoring limited:', error);
        }

        // Cleanup
        return () => {
            lcpObserver.disconnect();
            fidObserver.disconnect();
            clsObserver.disconnect();
        };
    }, [location]);
};

export default usePerformanceMonitoring;
